import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import LegacyPurchaseReceiptImportModal from '../components/LegacyPurchaseReceiptImportModal';
import {
  CukcukExportTemplateError,
  CukcukExportValidationError,
  exportPurchaseReceiptToCukcuk,
  exportPurchaseReceiptsToCukcukZip,
} from '../services/cukcukExportService';
import type { CukcukBatchSkippedReceipt } from '../services/cukcukExportService';
import { dataEntryUserService } from '../services/dataEntryUserService';
import { purchaseReceiptService } from '../services/purchaseReceiptService';
import { storeService } from '../services/storeService';
import { supplierService } from '../services/supplierService';
import { warehouseGoodsCatalogService } from '../services/warehouseGoodsCatalogService';
import type { DataEntryUser } from '../types/dataEntryUser';
import type { PurchaseReceipt, PurchaseReceiptItemInput } from '../types/purchaseReceipt';
import type { Store } from '../types/store';
import type { Supplier } from '../types/supplier';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import { summarizeReceiptSuppliers } from '../utils/legacyPurchaseReceiptCandidates';

interface ReceiptFormValues {
  code: string;
  date: Dayjs;
  storeLocationId: string;
  supplierId?: string;
  supplierName?: string;
  note?: string;
  status: 'draft' | 'confirmed' | 'cancelled';
  items: Array<{
    materialId: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    expiryDate?: Dayjs;
    note?: string;
    sourcePurchaseId?: string;
  }>;
}

const getCurrentUserId = (): string => {
  for (const key of ['dataEntryUserId', 'userId', 'localId', 'zaloUserId']) {
    const value = localStorage.getItem(key)?.trim();
    if (value) return value;
  }
  return 'admin8amcoffee';
};

const fallbackUser = (): DataEntryUser => ({
  userId: getCurrentUserId(),
  name: 'Admin 8AM',
  avatar: '',
  isAdmin: true,
  dataEntryRole: true,
  createdAt: new Date(),
  updatedAt: new Date(),
});

const statusLabel: Record<PurchaseReceipt['status'], string> = {
  draft: 'Nháp',
  confirmed: 'Đã ghi nhận',
  cancelled: 'Đã hủy',
};

const nextReceiptCode = (receipts: PurchaseReceipt[]): string => {
  const prefix = `NH-${dayjs().format('YYYYMMDD')}-`;
  const sequence = receipts
    .map((receipt) => receipt.code)
    .filter((code) => code.startsWith(prefix))
    .map((code) => Number(code.slice(prefix.length)))
    .filter(Number.isFinite)
    .reduce((max, current) => Math.max(max, current), 0) + 1;
  return `${prefix}${String(sequence).padStart(3, '0')}`;
};

const PurchaseReceiptManagementPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<ReceiptFormValues>();
  const [receipts, setReceipts] = useState<PurchaseReceipt[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [materials, setMaterials] = useState<WarehouseGoodsItem[]>([]);
  const [currentUser, setCurrentUser] = useState<DataEntryUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [editorLoading, setEditorLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [legacyImportOpen, setLegacyImportOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [batchExporting, setBatchExporting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedReceiptIds, setSelectedReceiptIds] = useState<string[]>([]);
  const [exportPeriod, setExportPeriod] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [exportPeriodType, setExportPeriodType] = useState<'day' | 'month' | 'year'>('month');
  const [readyExport, setReadyExport] = useState<{
    filename: string;
    url: string;
    supplierName?: string;
    skipped?: CukcukBatchSkippedReceipt[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editorSupplierWarning, setEditorSupplierWarning] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [receiptRows, storeRows, supplierRows, materialRows] = await Promise.all([
        purchaseReceiptService.getReceipts(),
        storeService.getStores(),
        supplierService.getActiveSuppliers(),
        warehouseGoodsCatalogService.getAll(),
      ]);
      setReceipts(receiptRows);
      setStores(storeRows);
      setSuppliers(supplierRows);
      setMaterials(materialRows);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Không thể tải dữ liệu phiếu nhập');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    dataEntryUserService.getUserByUserId(getCurrentUserId())
      .then((user) => { if (active) setCurrentUser(user || fallbackUser()); })
      .catch(() => { if (active) setCurrentUser(fallbackUser()); });
    void loadData();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setSelectedReceiptIds([]);
  }, [exportPeriod, exportPeriodType]);

  const canUseReceipts = Boolean(currentUser?.isAdmin || currentUser?.dataEntryRole);
  const storeNames = useMemo(
    () => new Map(stores.map((store) => [store.id, store.name])),
    [stores],
  );
  const materialMap = useMemo(
    () => new Map(materials.map((material) => [material.id, material])),
    [materials],
  );
  const filteredReceipts = useMemo(
    () => receipts.filter((receipt) => dayjs(receipt.date).isSame(exportPeriod, exportPeriodType)),
    [receipts, exportPeriod, exportPeriodType],
  );

  const openCreate = () => {
    setEditingId(null);
    setEditorSupplierWarning(null);
    form.resetFields();
    form.setFieldsValue({
      code: nextReceiptCode(receipts),
      date: dayjs(),
      status: 'draft',
      items: [{ materialId: '', quantity: 1, unit: '', unitPrice: 0 }],
    });
    setEditorOpen(true);
  };

  const openEdit = async (receipt: PurchaseReceipt) => {
    if (!receipt.id) {
      messageApi.error('Phiếu nhập thiếu mã định danh');
      return;
    }
    setEditorLoading(true);
    setEditingId(receipt.id);
    setEditorSupplierWarning(null);
    setEditorOpen(true);
    try {
      const details = await purchaseReceiptService.getReceiptById(receipt.id);
      if (!details) throw new Error('Không tìm thấy phiếu nhập');
      form.setFieldsValue({
        code: details.receipt.code,
        date: dayjs(details.receipt.date),
        storeLocationId: details.receipt.storeLocationId,
        supplierId: details.receipt.supplierId || undefined,
        supplierName: details.receipt.supplierName || undefined,
        note: details.receipt.note || undefined,
        status: details.receipt.status,
        items: details.items.map((item) => ({
          materialId: item.materialId,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          expiryDate: item.expiryDate ? dayjs(item.expiryDate) : undefined,
          note: item.note || undefined,
          sourcePurchaseId: item.sourcePurchaseId || undefined,
        })),
      });
      const supplierSummary = summarizeReceiptSuppliers(details.items, materials);
      const supplierNames = supplierSummary.suppliers.map((supplier) => supplier.supplierName);
      if (supplierSummary.suppliers.length > 1 || supplierSummary.missingMaterialCount > 0) {
        const mappedNames = supplierNames.length ? supplierNames.join(', ') : 'chưa xác định';
        const missingText = supplierSummary.missingMaterialCount
          ? ` Có ${supplierSummary.missingMaterialCount} dòng chưa có mapping nhà cung cấp.`
          : '';
        setEditorSupplierWarning(
          `Phiếu này có nhiều hoặc thiếu nhà cung cấp (${mappedNames}). CUKCUK yêu cầu mỗi phiếu chỉ có một nhà cung cấp; hãy tách thành các phiếu riêng trước khi xuất.${missingText}`,
        );
      } else if (!details.receipt.supplierName && supplierNames.length === 1) {
        setEditorSupplierWarning(`Có thể gán nhà cung cấp ${supplierNames[0]} cho phiếu này.`);
      } else {
        setEditorSupplierWarning(null);
      }
    } catch (loadError) {
      messageApi.error(loadError instanceof Error ? loadError.message : 'Không thể tải chi tiết phiếu nhập');
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveReceipt = async (values: ReceiptFormValues) => {
    if (!currentUser || !canUseReceipts) return;
    setSaving(true);
    try {
      const items: PurchaseReceiptItemInput[] = values.items.map((item) => ({
        materialId: item.materialId,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        expiryDate: item.expiryDate?.toDate() || null,
        note: item.note || null,
        sourcePurchaseId: item.sourcePurchaseId || null,
      }));
      const input = {
        code: values.code,
        date: values.date.toDate(),
        storeLocationId: values.storeLocationId,
        supplierId: values.supplierId || null,
        supplierName: values.supplierName || null,
        createdBy: currentUser.userId,
        note: values.note || null,
        status: values.status,
        items,
      } as const;
      if (editingId) {
        await purchaseReceiptService.updateReceiptWithItems(editingId, input);
      } else {
        await purchaseReceiptService.createReceipt(input);
      }
      messageApi.success(editingId ? 'Đã cập nhật phiếu nhập' : 'Đã tạo phiếu nhập');
      setEditorOpen(false);
      await loadData();
    } catch (saveError) {
      messageApi.error(saveError instanceof Error ? saveError.message : 'Không thể lưu phiếu nhập');
    } finally {
      setSaving(false);
    }
  };

  const clearReadyExport = () => {
    if (readyExport) URL.revokeObjectURL(readyExport.url);
    setReadyExport(null);
  };

  const exportReceipt = async (receipt: PurchaseReceipt) => {
    if (!receipt.id) {
      messageApi.error('Phiếu nhập thiếu mã định danh');
      return;
    }
    if (exportingId) return;
    setExportingId(receipt.id);
    try {
      const result = await exportPurchaseReceiptToCukcuk(receipt.id);
      if (readyExport) URL.revokeObjectURL(readyExport.url);
      setReadyExport({ filename: result.filename, url: URL.createObjectURL(result.blob), supplierName: result.supplierName });
      messageApi.success('File Excel CUKCUK đã sẵn sàng');
    } catch (exportError) {
      if (exportError instanceof CukcukExportValidationError) {
        Modal.error({
          title: 'Chưa thể xuất CUKCUK',
          content: (
            <Space direction="vertical" data-testid="cukcuk-validation-errors">
              {exportError.errors.map((item, index) => (
                <Typography.Text key={`${item.field}-${item.itemId || index}`}>
                  {item.materialName ? `${item.materialName}: ` : ''}{item.message}
                </Typography.Text>
              ))}
            </Space>
          ),
          okText: 'Đi đến cấu hình',
          onOk: () => navigate('/cukcuk/mapping'),
        });
      } else if (exportError instanceof CukcukExportTemplateError) {
        messageApi.error(exportError.message);
      } else {
        messageApi.error(exportError instanceof Error ? exportError.message : 'Không thể xuất file CUKCUK');
      }
    } finally {
      setExportingId(null);
    }
  };

  const exportPeriodReceipts = async () => {
    const receiptIds = filteredReceipts
      .map((receipt) => receipt.id)
      .filter((id): id is string => Boolean(id));
    if (!receiptIds.length || batchExporting || exportingId) return;
    setBatchExporting(true);
    try {
      const result = await exportPurchaseReceiptsToCukcukZip(receiptIds);
      if (readyExport) URL.revokeObjectURL(readyExport.url);
      const skipped = result.skipped || [];
      setReadyExport({
        filename: result.filename,
        url: URL.createObjectURL(result.blob),
        supplierName: result.supplierName,
        skipped,
      });
      if (skipped.length) {
        messageApi.warning(`Đã tạo ZIP gồm ${receiptIds.length - skipped.length} phiếu; bỏ qua ${skipped.length} phiếu thiếu dữ liệu CUKCUK`);
      } else {
        messageApi.success(`Đã tạo file ZIP gồm ${receiptIds.length} phiếu CUKCUK`);
      }
    } catch (exportError) {
      if (exportError instanceof CukcukExportValidationError) {
        Modal.error({
          title: 'Chưa thể xuất CUKCUK',
          content: (
            <Space direction="vertical" data-testid="cukcuk-batch-validation-errors">
              {exportError.errors.map((item, index) => (
                <Typography.Text key={`${item.receiptId || 'receipt'}-${item.field}-${index}`}>
                  {item.receiptId ? `${item.receiptId}: ` : ''}{item.materialName ? `${item.materialName}: ` : ''}{item.message}
                </Typography.Text>
              ))}
            </Space>
          ),
          okText: 'Đi đến cấu hình',
          onOk: () => navigate('/cukcuk/mapping'),
        });
      } else {
        messageApi.error(exportError instanceof Error ? exportError.message : 'Không thể xuất file CUKCUK');
      }
    } finally {
      setBatchExporting(false);
    }
  };

  const deleteReceipt = (receipt: PurchaseReceipt) => {
    if (!receipt.id) return;
    Modal.confirm({
      title: 'Xóa phiếu nhập?',
      content: `Phiếu ${receipt.code} và toàn bộ nguyên liệu trong phiếu sẽ bị xóa. Thao tác này phù hợp để dọn phiếu test và không thể hoàn tác.`,
      okText: 'Xóa phiếu',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await purchaseReceiptService.deleteReceipt(receipt.id as string);
          messageApi.success(`Đã xóa phiếu ${receipt.code}`);
          await loadData();
        } catch (deleteError) {
          messageApi.error(deleteError instanceof Error ? deleteError.message : 'Không thể xóa phiếu nhập');
        }
      },
    });
  };

  const deleteSelectedReceipts = () => {
    if (!canUseReceipts || !selectedReceiptIds.length || bulkDeleting) return;
    const selectedReceipts = receipts.filter((receipt) => receipt.id && selectedReceiptIds.includes(receipt.id));
    if (!selectedReceipts.length) {
      setSelectedReceiptIds([]);
      return;
    }
    const receiptCodes = selectedReceipts.map((receipt) => receipt.code).join(', ');
    Modal.confirm({
      title: `Xóa ${selectedReceipts.length} phiếu nhập?`,
      content: `Các phiếu ${receiptCodes} và toàn bộ nguyên liệu trong đó sẽ bị xóa. Thao tác này không thể hoàn tác.`,
      okText: `Xóa ${selectedReceipts.length} phiếu`,
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        setBulkDeleting(true);
        try {
          const deletedCount = await purchaseReceiptService.deleteReceipts(selectedReceipts.map((receipt) => receipt.id as string));
          setSelectedReceiptIds([]);
          messageApi.success(`Đã xóa ${deletedCount} phiếu nhập`);
          await loadData();
        } catch (deleteError) {
          messageApi.error(deleteError instanceof Error ? deleteError.message : 'Không thể xóa các phiếu nhập đã chọn');
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedReceiptIds,
    onChange: (keys: React.Key[]) => setSelectedReceiptIds(keys.map(String)),
  };

  const columns: ColumnsType<PurchaseReceipt> = [
    { title: 'Mã phiếu', dataIndex: 'code', key: 'code', render: (value) => <Typography.Text strong>{value}</Typography.Text> },
    { title: 'Ngày', dataIndex: 'date', key: 'date', render: (value: Date) => value.toLocaleDateString('vi-VN') },
    { title: 'Cơ sở', dataIndex: 'storeLocationId', key: 'store', render: (value) => storeNames.get(value) || value },
    { title: 'Nhà cung cấp', dataIndex: 'supplierName', key: 'supplierName', render: (value?: string) => value || <Tag color="orange">Chưa có</Tag> },
    { title: 'Trạng thái', dataIndex: 'status', key: 'status', render: (value: PurchaseReceipt['status']) => <Tag>{statusLabel[value]}</Tag> },
    {
      title: 'Thao tác', key: 'actions', width: 240,
      render: (_, receipt) => (
        <Space wrap data-testid={`receipt-actions-${receipt.id || 'unknown'}`}>
          <Button icon={<EditOutlined />} onClick={() => void openEdit(receipt)} disabled={!canUseReceipts}>Sửa</Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => deleteReceipt(receipt)} disabled={!canUseReceipts}>Xóa</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => void exportReceipt(receipt)}
            loading={exportingId === receipt.id}
            disabled={!canUseReceipts || Boolean(exportingId)}
          >Xuất CUKCUK</Button>
        </Space>
      ),
    },
  ];

  return (
    <AdminLayout>
      {contextHolder}
      <Modal
        open={Boolean(readyExport)}
        title="File Excel CUKCUK đã sẵn sàng"
        footer={null}
        onCancel={clearReadyExport}
      >
        <Space direction="vertical">
          <Typography.Text>Nhấn nút bên dưới để tải file về máy.</Typography.Text>
          <Alert
            type="warning"
            showIcon
            message="Thứ tự nhập trên CUKCUK rất quan trọng"
            description="Mẫu Excel CUKCUK không có cột nhà cung cấp. Trong CUKCUK, hãy chọn đúng ngày nhập và nhà cung cấp ở phần đầu phiếu trước, rồi mới bấm Nhập khẩu và chọn file; tên nhà cung cấp đã được ghi trong tên file để đối chiếu. Nếu giá vẫn về 0, kiểm tra Thiết lập chính sách giá NVL của nhà cung cấp 8am."
          />
          {readyExport?.skipped?.length ? (
            <Alert
              data-testid="batch-export-skipped"
              type="warning"
              showIcon
              message={`Bá» qua ${readyExport.skipped.length} phiáº¿u do thiáº¿u dá»¯ liá»‡u CUKCUK`}
              description={(
                <Space direction="vertical" size={0}>
                  {readyExport.skipped.slice(0, 10).map((skipped) => {
                    const receipt = receipts.find((item) => item.id === skipped.receiptId);
                    return (
                      <Typography.Text key={skipped.receiptId}>
                        {receipt?.code || skipped.receiptId}: {skipped.errors[0]?.message || 'Dá»¯ liá»‡u khÃ´ng há»£p lá»‡'}
                      </Typography.Text>
                    );
                  })}
                  {readyExport.skipped.length > 10 && <Typography.Text type="secondary">VÃ  cÃ²n {readyExport.skipped.length - 10} phiáº¿u khÃ¡c.</Typography.Text>}
                </Space>
              )}
            />
          ) : null}
          {readyExport && (
            <>
              <Space direction="vertical" size={4}>
                {readyExport.supplierName && <Typography.Text>Nhà cung cấp: {readyExport.supplierName}</Typography.Text>}
                <Typography.Text>Tên file: <Typography.Text code>{readyExport.filename}</Typography.Text></Typography.Text>
              </Space>
              <a
                href={readyExport.url}
                download={readyExport.filename}
                onClick={() => window.setTimeout(clearReadyExport, 1000)}
              >
                <Button type="primary" icon={<DownloadOutlined />}>Tải file Excel</Button>
              </a>
            </>
          )}
        </Space>
      </Modal>
      <div style={{ padding: '24px 0' }}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Typography.Title level={2} style={{ margin: 0 }}>Phiếu nhập nguyên liệu</Typography.Title>
              <Typography.Text type="secondary">Tạo phiếu nhập, ghi nhận nhiều nguyên liệu và xuất đúng template CUKCUK.</Typography.Text>
            </div>
            <Space data-testid="receipt-toolbar">
              <Button icon={<SettingOutlined />} onClick={() => navigate('/cukcuk/mapping')}>Cấu hình CUKCUK</Button>
              <Button onClick={() => setLegacyImportOpen(true)} disabled={!canUseReceipts}>Tạo từ dữ liệu cũ</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!canUseReceipts}>Tạo phiếu nhập</Button>
            </Space>
          </div>

          {!canUseReceipts && currentUser && <Alert type="warning" showIcon message="Tài khoản hiện tại chưa được cấp quyền nhập liệu." />}
          {error && <Alert type="error" showIcon message={error} />}
           <Card>
             <Space wrap style={{ marginBottom: 16 }}>
               <Typography.Text strong>Xuất theo:</Typography.Text>
               <Segmented
                 value={exportPeriodType}
                 data-testid="receipt-export-period-type"
                 options={[{ label: 'Ngày', value: 'day' }, { label: 'Tháng', value: 'month' }, { label: 'Năm', value: 'year' }]}
                 onChange={(value) => {
                   const nextType = value as 'day' | 'month' | 'year';
                   setExportPeriodType(nextType);
                   setExportPeriod(dayjs().startOf(nextType));
                 }}
               />
               <DatePicker
                 data-testid="receipt-export-period"
                 picker={exportPeriodType === 'day' ? 'date' : exportPeriodType}
                 value={exportPeriod}
                 format={exportPeriodType === 'day' ? 'DD/MM/YYYY' : exportPeriodType === 'month' ? 'MM/YYYY' : 'YYYY'}
                 allowClear={false}
                 onChange={(value) => value && setExportPeriod(value.startOf(exportPeriodType))}
               />
               <Button
                 icon={<DownloadOutlined />}
                 onClick={() => void exportPeriodReceipts()}
                 loading={batchExporting}
                 disabled={!canUseReceipts || Boolean(exportingId) || batchExporting || !filteredReceipts.length}
               >Xuất {exportPeriodType === 'day' ? 'ngày' : exportPeriodType === 'month' ? 'tháng' : 'năm'} (ZIP)</Button>
               <Button
                 onClick={() => setSelectedReceiptIds(filteredReceipts.map((receipt) => receipt.id).filter((id): id is string => Boolean(id)))}
                 disabled={!canUseReceipts || !filteredReceipts.length || bulkDeleting}
               >Chọn tất cả kỳ</Button>
               <Button onClick={() => setSelectedReceiptIds([])} disabled={!selectedReceiptIds.length || bulkDeleting}>Bỏ chọn</Button>
               <Button
                 danger
                 icon={<DeleteOutlined />}
                 onClick={deleteSelectedReceipts}
                 loading={bulkDeleting}
                 disabled={!canUseReceipts || !selectedReceiptIds.length || bulkDeleting}
               >{selectedReceiptIds.length ? `Xóa ${selectedReceiptIds.length} phiếu` : 'Xóa phiếu'}</Button>
             </Space>
             {loading ? <Spin /> : filteredReceipts.length ? (
              <Table
                rowKey="id"
                rowSelection={canUseReceipts ? rowSelection : undefined}
                columns={columns}
                dataSource={filteredReceipts}
                scroll={{ x: 850 }}
                pagination={{ pageSize: 10 }}
              />
            ) : <Empty description={receipts.length ? 'Không có phiếu nhập trong kỳ đã chọn' : 'Chưa có phiếu nhập'} />}
          </Card>
        </Space>
      </div>

      <LegacyPurchaseReceiptImportModal
        open={legacyImportOpen}
        onClose={() => setLegacyImportOpen(false)}
        onUpdated={loadData}
        onCreated={async (receipt) => {
          await loadData();
          await openEdit(receipt);
        }}
        receiptCodes={receipts.map((receipt) => receipt.code)}
        currentUserId={currentUser?.userId || ''}
        materials={materials}
        stores={stores}
      />

      <Modal
        title={editingId ? 'Sửa phiếu nhập' : 'Tạo phiếu nhập'}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        {editorLoading ? <Spin /> : (
          <Form form={form} layout="vertical" onFinish={(values) => void saveReceipt(values)}>
            {editorSupplierWarning && (
              <Alert
                data-testid="receipt-supplier-warning"
                type="warning"
                showIcon
                message="Kiểm tra nhà cung cấp của phiếu"
                description={editorSupplierWarning}
                style={{ marginBottom: 16 }}
              />
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16 }}>
              <Form.Item name="code" label="Mã phiếu" rules={[{ required: true, message: 'Nhập mã phiếu' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="date" label="Ngày nhập" rules={[{ required: true, message: 'Chọn ngày nhập' }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item name="storeLocationId" label="Cơ sở" rules={[{ required: true, message: 'Chọn cơ sở' }]}>
                <Select showSearch optionFilterProp="label" options={stores.map((store) => ({ value: store.id, label: store.name }))} />
              </Form.Item>
              <Form.Item name="status" label="Trạng thái" rules={[{ required: true }]}>
                <Select options={Object.entries(statusLabel).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
              <Form.Item name="supplierId" label="Nhà cung cấp" rules={[{ required: true, message: 'Vui lòng chọn nhà cung cấp' }]}>
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="Chọn nhà cung cấp"
                  options={suppliers.map((supplier) => ({ value: supplier.id, label: `${supplier.code} - ${supplier.name}`, supplier }))}
                  onChange={(supplierId) => {
                    const supplier = suppliers.find((item) => item.id === supplierId);
                    form.setFieldValue('supplierName', supplier?.name || undefined);
                  }}
                />
              </Form.Item>
              <Form.Item name="note" label="Ghi chú"><Input /></Form.Item>
              <Form.Item name="supplierName" hidden><Input /></Form.Item>
            </div>
            <Divider orientation="left">Nguyên liệu</Divider>
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {fields.map((field, index) => (
                    <Card key={field.key} size="small" title={`Dòng ${index + 1}`} extra={fields.length > 1 ? <Button type="link" danger onClick={() => remove(field.name)}>Xóa</Button> : null}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(210px, 2fr) 100px 120px 130px 150px', gap: 12, alignItems: 'start' }}>
                        <Form.Item {...field} name={[field.name, 'materialId']} label="Nguyên liệu" rules={[{ required: true, message: 'Chọn nguyên liệu' }]}>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={materials.map((material) => ({ value: material.id, label: `${material.name} (${material.productCode})` }))}
                            onChange={(materialId) => {
                              const material = materialMap.get(materialId);
                              form.setFieldValue(['items', index, 'unit'], material?.cukcukUnit || material?.baseUnit || '');
                              if (material?.supplierId && !form.getFieldValue('supplierId')) {
                                const supplier = suppliers.find((item) => item.id === material.supplierId);
                                form.setFieldsValue({
                                  supplierId: material.supplierId,
                                  supplierName: supplier?.name || material.supplierName || undefined,
                                });
                              }
                            }}
                          />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, 'quantity']} label="Số lượng" rules={[{ required: true, type: 'number', min: 0.0000001, message: '> 0' }]}>
                          <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, 'unit']} label="ĐVT" rules={[{ required: true, message: 'Nhập ĐVT' }]}>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, 'unitPrice']} label="Đơn giá" rules={[{ required: true, type: 'number', min: 0, message: '>= 0' }]}>
                          <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, 'expiryDate']} label="Hạn sử dụng">
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>
                      </div>
                      <Form.Item {...field} name={[field.name, 'note']} label="Ghi chú dòng" style={{ marginBottom: 0 }}>
                        <Input />
                      </Form.Item>
                    </Card>
                  ))}
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ quantity: 1, unitPrice: 0 })}>Thêm nguyên liệu</Button>
                </Space>
              )}
            </Form.List>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setEditorOpen(false)}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={saving}>Lưu phiếu nhập</Button>
            </div>
          </Form>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default PurchaseReceiptManagementPage;
